import React, { useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { slideFromBottom } from "../../ui/animations"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import {
  toggleTag,
  selectTagApplicationByObservationId,
  selectTagDescriptionsByName,
} from "../observations/slice"

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 0.5rem;
  animation: 0.5s ${slideFromBottom} ease-out;
`
const Tag = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  padding: 0.25rem 0.25rem 0.25rem 0.5rem;
  margin: 0 0.5rem;
  border-radius: 0.25rem;
  background-color: ${(props) => props.color};
  color: black;
  opacity: ${(props) => (props.selected ? "1.0" : "0.25")};
  transition: all 0.3s ease-out;
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  span {
    display: inline-block;
    min-width: 1.5rem;
    text-align: center;
    padding: 0.25rem;
    border-radius: 0.25rem;
    background-color: black;
    color: ${(props) => props.color};
    margin-left: 0.5rem;
  }
`
const Selectable = styled.div`
  flex: auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  ${Tag} {
    cursor: pointer;
    &:hover {
      opacity: 0.6;
    }
  }
`

const Auto = styled.div`
  flex: none;
  display: flex;
  flex-direction: row;
  align-items: stretch;
`

const Label = styled.div`
  flex: auto;
`
const Key = styled.div`
  flex: none;
  min-width: 1.5rem;
  text-align: center;
  padding: 0.25rem;
  border-radius: 0.25rem;
  background-color: black;
  color: ${(props) => props.color};
  margin-left: 0.5rem;
`
const LoadingContainer = styled.div`
  font-size: 1.5rem;
  min-width: 2rem;
`

const findTagByKey = (requestedKey, tags = {}) => {
  // eslint-disable-next-line
  return Object.values(tags).find((tag) => {
    return tag.key == requestedKey.toUpperCase()
  })
}

// A component to apply tags to an observation
const TagBar = ({ observationId, tags = [] }) => {
  const dispatch = useDispatch()
  const tagsDescriptionsByName = useSelector(selectTagDescriptionsByName)

  const tagApplicationByObsId = useSelector(selectTagApplicationByObservationId)
  const loadStatusUpdate = tagApplicationByObsId[observationId]?._loadStatus

  // We build up a map of the current tags by tag name
  let appliedTagsByName = tags?.reduce((r, t) => ({ ...r, [t.name]: t }), {})

  useEffect(() => {
    const listener = (event) => {
      switch (event.key) {
        default: {
          let tag = findTagByKey(event.key, tagsDescriptionsByName)
          if (tag) {
            // NOTE we don't set a value he we work out how to toggle in the action as this function is bound before it has data
            dispatch(
              toggleTag({
                observationId,
                name: tag.name,
              })
            )
          }
          break
        }
      }
    }
    document.addEventListener("keydown", listener)
    return () => document.removeEventListener("keydown", listener)
  }, [])

  const handleToggleTag = (name) => {
    dispatch(
      toggleTag({
        observationId,
        name,
      })
    )
  }

  return (
    <Container>
      <Selectable>
        {Object.values(tagsDescriptionsByName)
          .filter(({ manual }) => manual)
          .map((tagDesc) => (
            <Tag
              key={tagDesc.name}
              onClick={() => handleToggleTag(tagDesc.name)}
              color={tagDesc.colorCode}
              disabled={
                appliedTagsByName[tagDesc.name]?.manual == false
              }
              selected={
                appliedTagsByName[tagDesc.name] &&
                appliedTagsByName[tagDesc.name].value
              }
            >
              <Label>{tagDesc.name} </Label>
              {tagDesc.key && (
                <Key color={tagDesc.colorCode}>{tagDesc.key}</Key>
              )}
            </Tag>
          ))}
      </Selectable>
      <Auto>
        {Object.values(tagsDescriptionsByName)
          .filter(({ manual }) => !manual)
          .map((tagDescription) => (
            <Tag
              key={tagDescription.name}
              color={tagDescription.colorCode}
              disabled={
                appliedTagsByName[tagDescription.name]?.manual === false
              }
              selected={
                appliedTagsByName[tagDescription.name] &&
                appliedTagsByName[tagDescription.name].value
              }
            >
              <Label>{tagDescription.name} </Label>
            </Tag>
          ))}
      </Auto>
      <LoadingContainer>
        {loadStatusUpdate === "loading" && <LoadingSpinnerInline />}
      </LoadingContainer>
    </Container>
  )
}

TagBar.propTypes = {
  observationId: PropTypes.string,
  tags: PropTypes.array, // The tags to render
}

export default TagBar
